import { computed, ref, watch } from 'vue';
import { useCompaniesStore } from '@/stores/companies';
import api from '@/utils/api';
import { useI18n } from 'vue-i18n';

/**
 * Composable for managing companies
 */
export function useCompanies() {
  const store = useCompaniesStore();

  const companies = computed(() => store.companies);
  const loading = computed(() => store.loading);
  const error = computed(() => store.error);

  const loadCompanies = async (force = false) => {
    return await store.loadCompanies(force);
  };

  const createCompany = async (companyData) => {
    return await store.createCompany(companyData);
  };

  const deleteCompany = async (companyId) => {
    return await store.deleteCompany(companyId);
  };

  return {
    companies,
    loading,
    error,
    loadCompanies,
    createCompany,
    deleteCompany
  };
}

/**
 * Composable for managing vendors for a specific company
 */
export function useVendors(companyId) {
  const vendors = ref([]);
  const loading = ref(false);
  const error = ref(null);

  const loadVendors = async (id = companyId.value) => {
    if (!id) {
      vendors.value = [];
      return [];
    }

    loading.value = true;
    error.value = null;

    try {
      const response = await api.get(`/api/companies/${id}/vendors`);
      // API returns { data: [...] }
      vendors.value = response.data.data || response.data;
      return vendors.value;
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to load vendors';
      console.error('Failed to load vendors:', err);
      vendors.value = [];
      return [];
    } finally {
      loading.value = false;
    }
  };

  const createVendor = async (id, vendorData) => {
    try {
      const response = await api.post(`/api/companies/${id}/vendors`, vendorData);
      vendors.value.push(response.data);
      return response.data;
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to create vendor';
      throw err;
    }
  };

  // Auto-load vendors when companyId changes
  if (companyId) {
    watch(companyId, (newId) => {
      if (newId) {
        loadVendors(newId);
      } else {
        vendors.value = [];
      }
    }, { immediate: true });
  }

  return {
    vendors,
    loading,
    error,
    loadVendors,
    createVendor
  };
}

/**
 * Composable for invoice types
 */
export function useInvoiceTypes() {
  const { t } = useI18n();
  
  const types = computed(() => [
    { value: null, label: t('invoices.allTypes') },
    { value: 'expense', label: t('scanDetails.expense') + ' / ' + t('invoices.purchase') },
    { value: 'income', label: t('scanDetails.income') + ' / ' + t('invoices.sale') }
  ]);

  const getTypeLabel = (type) => {
    const found = types.value.find(t => t.value === type);
    return found ? found.label : t('scanDetails.notSpecified');
  };

  const getTypeBadgeClass = (type) => {
    if (type === 'expense') return 'expense';
    if (type === 'income') return 'income';
    return '';
  };

  return {
    types,
    getTypeLabel,
    getTypeBadgeClass
  };
}
